/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import type { ProductData } from "./types";
import { EXCLUDED_PRODUCTS } from "./constants";
import { AngleDownFaIcon } from "@/components/common/icons";

/** @deprecated Use AngleDownFaIcon from shared icons directly */
const AngleDownIcon = AngleDownFaIcon;

export { AngleDownIcon };

interface ProductDropdownProps {
  productData: ProductData | null;
  onNavClick: () => void;
}

/**
 * S-1: Window in milliseconds during which a click immediately following a
 * hover-open is treated as part of the same gesture and the toggle is
 * suppressed. See NavDropdown.tsx for full rationale (proximity-sensing on
 * Surface-class hybrids and touchpad-only taps on touchscreen laptops).
 * 250ms is well above tap-gesture duration (~80–180ms) and well below
 * deliberate mouse-hover-then-click (400–600ms), so mouse interactions are
 * unaffected.
 */
const HOVER_CLICK_DEBOUNCE_MS = 250;

/**
 * Returns true only for mouse or pen pointer types.
 * Filters out touch events so onPointerEnter/Leave only open the dropdown
 * via hover-capable inputs on Chrome 133+ and Firefox on Windows touch-capable
 * devices (where onMouseEnter can be suppressed due to pointer type
 * misclassification — Chromium bug, confirmed Feb 2025).
 */
const isHoverCapable = (e: React.PointerEvent): boolean =>
  e.pointerType === "mouse" || e.pointerType === "pen";

const ProductDropdown: React.FC<ProductDropdownProps> = ({
  productData,
  onNavClick,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // B-1 fix: onTouchStart fires before pointer events — set flag early enough
  // to guard the entire touch event sequence (touchstart → pointerdown →
  // mousedown → click → pointerup). 300ms covers the full sequence on
  // throttled hardware where rAF (~16ms) cleared the flag too early.
  const touchInProgress = useRef(false);
  // S-1: timestamp of the most recent hover-open. Click events arriving
  // within HOVER_CLICK_DEBOUNCE_MS of a hover-open are treated as part of
  // the same gesture and the toggle is suppressed.
  const lastHoverOpenAt = useRef<number>(0);

  return (
    <div
      className="items product-container"
      role="button"
      tabIndex={0}
      onTouchStart={() => {
        touchInProgress.current = true;
      }}
      onClick={() => {
        // S-1: Suppress the toggle-to-close that would cancel a
        // hover-open when a misclassified touch tap (proximity-sensing or
        // touchpad-only) fires pointerenter before the click.
        const openedAt = lastHoverOpenAt.current;
        const withinDebounce = Date.now() - openedAt < HOVER_CLICK_DEBOUNCE_MS;
        if (!withinDebounce) {
          setOpen((prev) => !prev);
        }
        if (touchInProgress.current) {
          setTimeout(() => {
            touchInProgress.current = false;
          }, 300);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen(!open);
        } else if (e.key === "Escape" && open) {
          e.preventDefault();
          setOpen(false);
        }
      }}
      onPointerEnter={(e) => {
        if (isHoverCapable(e) && !touchInProgress.current) {
          lastHoverOpenAt.current = Date.now();
          setOpen(true);
        }
      }}
      onPointerLeave={(e) => {
        if (isHoverCapable(e) && !touchInProgress.current) setOpen(false);
      }}
      onFocus={() => {
        // Issue 3: guard focus-open during a touch gesture so one tap opens the
        // menu (focus + click would otherwise open then immediately toggle it
        // closed). Keyboard Tab focus is unaffected — the flag is touch-only.
        if (!touchInProgress.current) setOpen(true);
      }}
      onBlur={(e) => {
        // Only close if focus truly left the entire container, not just
        // moved to a child element inside the dropdown panel.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
      aria-haspopup="true"
      aria-expanded={open}
    >
      <span>{t("header.product")}</span>
      <AngleDownIcon />
      {open && !productData && (
        <div className="allproducts">
          <div className="product-dropdown-loading">
            {t("header.products_loading")}
          </div>
        </div>
      )}
      {open && productData && (
        <div className="allproducts">
          <div className="product-dropdown">
            {Object.entries(productData).map(([category, products]) => {
              const activeProducts = products.filter(
                (product) => !EXCLUDED_PRODUCTS.includes(product.product_name),
              );

              if (activeProducts.length === 0) return null;

              return (
                <div className="product-block" key={category}>
                  <div className="product-content">
                    <p className="product-type">{category}</p>
                    <div className="product-list">
                      {activeProducts.map((product) => (
                        <React.Fragment key={product.product_slug}>
                          <Link
                            className="product-detail product-detail-link"
                            onClick={onNavClick}
                            to={AddLangToPath(`/${product.product_slug}`)}
                          >
                            {/* WCAG SC 1.3.1: product name is styled nav text,
                                not a content heading — span avoids heading pollution. */}
                            <span className="product-name">
                              {product.product_name}
                            </span>
                            <p className="product-description">
                              {product.product_title}
                            </p>
                          </Link>
                          <hr />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDropdown;
